import React, { useState, useEffect } from "react";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiSelect,
  EuiButton,
  EuiText,
  EuiInlineEditTitle,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCheckboxGroup,
  EuiSwitch,
} from "@elastic/eui";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";

const PRESENTATION_STYLE_OPTIONS = [
  { id: "narrative", label: "Narrative" },
  { id: "bullet_points", label: "Bullet Points" },
  { id: "diagrams", label: "Diagrams" },
  { id: "real_world_examples", label: "Real-World Examples" },
];

const EditAgentForm = ({ config, course, onNameChange, onSettingsUpdate }) => {
  const [form, setForm] = useState({
    config_name: config.config_name,
    course_name: course.course_name,
    course_number: course.course_number,
  });
  const [settings, setSettings] = useState({
    mode: config.mode || "step-by-step",
    default_assistance_level: config.default_assistance_level || 1,
    subject_domain: config.subject_domain ?? "",
    grade_level: config.grade_level ?? "",
  });
  const [presentationStyles, setPresentationStyles] = useState(
    config.presentation_styles || []
  );
  const [studentStyleChoiceEnabled, setStudentStyleChoiceEnabled] = useState(
    config.student_style_choice_enabled || false
  );
  const [isSavingStyles, setIsSavingStyles] = useState(false);

  const [preName, setPreName] = useState(config.config_name);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    setForm({
      config_name: config.config_name,
      course_name: course.course_name,
      course_number: course.course_number,
    });
    setSettings({
      mode: config.mode || "step-by-step",
      default_assistance_level: config.default_assistance_level || 1,
      subject_domain: config.subject_domain ?? "",
      grade_level: config.grade_level ?? "",
    });
    setPresentationStyles(config.presentation_styles || []);
    setStudentStyleChoiceEnabled(config.student_style_choice_enabled || false);
    setPreName(config.config_name);
  }, [config, course.course_name, course.course_number]);

  const sendUpdateRequest = async (url, payload) => {
    const response = await fetch(url, PostOptions(payload));
    const text = await response.json();
    if (!response.ok) {
      throw new Error(text.message || text.error || "Request failed");
    }
    return text;
  };

  const changeName = async () => {
    try {
      setIsRenaming(true);
      setErrorMessage(null);
      await sendUpdateRequest(getApiUrl("/api/agent/name/edit"), {
        config_name: preName,
        course_name: course.course_name,
        new_name: form.config_name,
      });
      onNameChange(preName, form.config_name);
      setPreName(form.config_name);
    } catch (error) {
      console.error("There was an error!", error);
      setErrorMessage(error.message);
    } finally {
      setIsRenaming(false);
    }
  };

  const toggleSettingsEditor = () => {
    if (isEditingSettings) {
      setSettings({
        mode: config.mode || "step-by-step",
        default_assistance_level: config.default_assistance_level || 1,
        subject_domain: config.subject_domain ?? "",
        grade_level: config.grade_level ?? "",
      });
      setPresentationStyles(config.presentation_styles || []);
      setStudentStyleChoiceEnabled(config.student_style_choice_enabled || false);
    }
    setIsEditingSettings((prev) => !prev);
  };

  const handleStyleCheckboxChange = (optionId) => {
    setPresentationStyles((prev) =>
      prev.includes(optionId)
        ? prev.filter((s) => s !== optionId)
        : [...prev, optionId]
    );
  };

  const saveAgentSettings = async () => {
    try {
      setIsSavingSettings(true);
      setErrorMessage(null);
      await sendUpdateRequest(getApiUrl("/api/agent/mode/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        mode: settings.mode,
      });
      await sendUpdateRequest(getApiUrl("/api/agent/assistance-level/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        default_assistance_level: settings.default_assistance_level,
      });
      await sendUpdateRequest(getApiUrl("/api/agent/context/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        subject_domain: settings.subject_domain,
        grade_level: settings.grade_level,
      });
      await sendUpdateRequest(getApiUrl("/api/agent/presentation-styles/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        presentation_styles: presentationStyles,
        student_style_choice_enabled: studentStyleChoiceEnabled,
      });
      onSettingsUpdate?.({
        mode: settings.mode,
        default_assistance_level: settings.default_assistance_level,
        subject_domain: settings.subject_domain,
        grade_level: settings.grade_level,
        presentation_styles: presentationStyles,
        student_style_choice_enabled: studentStyleChoiceEnabled,
      });
      setIsEditingSettings(false);
    } catch (error) {
      console.error("Failed to update agent settings", error);
      setErrorMessage(error.message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <EuiFlexGroup direction="column" gutterSize="xs">
      <EuiFlexItem>
        <EuiInlineEditTitle
          inputAriaLabel="Edit title inline"
          value={form.config_name}
          onChange={(e) => setForm({ ...form, config_name: e.target.value })}
          onSave={changeName}
          onCancel={() => setForm({ ...form, config_name: preName })}
          isLoading={isRenaming}
          heading="h3"
          size="s"
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          onClick={toggleSettingsEditor}
          iconType={isEditingSettings ? "cross" : "pencil"}
          color="primary"
        >
          {isEditingSettings ? "Close Agent Settings" : "Edit Agent Settings"}
        </EuiButton>
      </EuiFlexItem>
      {isEditingSettings && (
        <EuiFlexItem>
          <EuiForm
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              saveAgentSettings();
            }}
          >
            <EuiFormRow label="Default AI Assistance Level">
              <EuiSelect
                options={[
                  { value: 1, text: "Level 1 - Hint Only (concepts and hints)" },
                  { value: 2, text: "Level 2 - Review (feedback only)" },
                  { value: 3, text: "Level 3 - Troubleshoot (find and fix errors)" },
                  { value: 4, text: "Level 4 - Co-Builder (work through together)" },
                  { value: 5, text: "Level 5 - Full Solutions (learning only)" },
                ]}
                value={settings.default_assistance_level}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_assistance_level: parseInt(e.target.value, 10),
                  })
                }
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiFormRow label="Subject domain" helpText="Feeds into system prompt for age-appropriate vocabulary.">
              <EuiFieldText
                value={settings.subject_domain}
                onChange={(e) => setSettings({ ...settings, subject_domain: e.target.value })}
                placeholder="e.g. Mathematics"
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiFormRow label="Grade level" helpText="Feeds into system prompt for complexity.">
              <EuiFieldText
                value={settings.grade_level}
                onChange={(e) => setSettings({ ...settings, grade_level: e.target.value })}
                placeholder="e.g. High school (9-12)"
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiFormRow
              label="Presentation Styles"
              helpText="Select how content is delivered to students. Multiple selections are combined."
            >
              <EuiCheckboxGroup
                options={PRESENTATION_STYLE_OPTIONS}
                idToSelectedMap={Object.fromEntries(
                  PRESENTATION_STYLE_OPTIONS.map((opt) => [
                    opt.id,
                    presentationStyles.includes(opt.id),
                  ])
                )}
                onChange={handleStyleCheckboxChange}
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiFormRow
              helpText="When enabled, students can pick their own presentation style in the chat."
            >
              <EuiSwitch
                label="Let students choose their own style"
                checked={studentStyleChoiceEnabled}
                onChange={(e) => setStudentStyleChoiceEnabled(e.target.checked)}
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiButton
              fill
              type="submit"
              isLoading={isSavingSettings}
              disabled={isSavingSettings}
            >
              Save Settings
            </EuiButton>
          </EuiForm>
        </EuiFlexItem>
      )}
      <EuiFlexItem>
        <EuiText color="danger">{errorMessage}</EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

export default EditAgentForm;
